// @givmo/passkeys — whether a sign-in lock binds the signed-in account, and what the account-wide
// surfaces say while one does (AS-WEB23; frames E1 and E3 of the approved design page, D-AS-48).
//
// THE SIGNAL is AS-BE81's account-level answer on `GET /users/me/security`: `sign_in_lock.active`,
// true while ANY organization's sign-in lock binds the account (AS-BE80's predicate, the one the
// passkey and emailed-code mints refuse on). The backend serves it on its two full reads whatever
// `EMPLOYER_SSO_ENABLED` says, and null on the four narrow routes. The committed snapshot does not
// declare it yet, so it is read by hand off `unknown`, and only `true` counts: absent (a backend that
// predates it) or anything unreadable is not locked.
//
// ⚠ THE SERVER'S NULL IS "NOT STATED", NEVER "NOT LOCKED" (backend `employer_sso.md` §10). A full
// read always states the lock, so a null here can only be a narrow route's answer written over it,
// and the one the web calls, the factor-reset cancel, keeps the full read's statement where it
// writes (`recordFactorResetCancelled`). Read as not locked, its null turned a lock-bound card back
// into today's card (cut 1009b's review, F1).
//
// NEVER the employer-only `employer_sso.lock_active` for the decision (null while the flag is off or
// there is no employer, so it misses the rollback state and any other organization's lock), and never
// the `gl` claim. The employer block is read for one thing only: whether the words may NAME the
// organization. Signed in, the card names the company, as the frames do, but only where the
// employer's own block says its lock is the one that binds. Otherwise (the rollback state, with
// `sign_in_lock.active` beside `employer_sso: null`, or another organization's lock) the words name no
// one: "Your organization …", the plan's organization-neutral words for an unnamed lock.
//
// Under a lock the surfaces offer no setup, and no door either: the card and the offer say why and
// stop. A way back in (the escape hatch) lives on the sign-in pages, never here.
//
// Words, per line (the design page's string table): every one is DRAFT. D-AS-48 (4) approved the
// frames' drafts as written, kept DRAFT until Jonathan's copy sweep; the unnamed subject is new here,
// for the same sweep.

import { useMemo } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { isGivmoError, type UserSecurityOut } from '@givmo/api-client';
import {
  fetchMySecurityNow,
  servedData,
  useMySecurity,
  type AccountReadOptions,
} from './account-factors';

/** Whether a sign-in lock binds the account, and whom its words may name. */
export interface SignInLock {
  /** AS-BE81's `sign_in_lock.active`, read `=== true`. */
  readonly active: boolean;
  /**
   * The organization the words name: the employer, only where its own block says its lock binds
   * (`employer_sso.lock_active` with a `company_name`). `null` names no one.
   */
  readonly organizationName: string | null;
}

const NOT_LOCKED: SignInLock = { active: false, organizationName: null };

/** The lock as a served security read states it; not locked while unknown, dark or unstated. */
export function signInLockFrom(security: UserSecurityOut | undefined): SignInLock {
  const body = security as
    | {
        sign_in_lock?: { active?: unknown } | null;
        employer_sso?: { lock_active?: unknown; company_name?: unknown } | null;
      }
    | undefined;
  if (body?.sign_in_lock?.active !== true) return NOT_LOCKED;
  const employer = body.employer_sso;
  const name = typeof employer?.company_name === 'string' ? employer.company_name.trim() : '';
  return {
    active: true,
    organizationName: employer?.lock_active === true && name.length > 0 ? name : null,
  };
}

/**
 * The lock as the account's security read states it (the card's own read, or the cache's). The same
 * object while the read is unchanged, so a host may memoize on it.
 */
export function useSignInLock(uid: string | null, options: AccountReadOptions = {}): SignInLock {
  const security = servedData(useMySecurity(uid, options));
  return useMemo(() => signInLockFrom(security), [security]);
}

/**
 * Whether the account's security read has answered, so the lock is known: a 200, or the dark 404
 * (not locked, as before). Until then a surface draws no action it would withdraw once a lock binds
 * (cut 1009b's review, F3). A first read that FAILS has not answered: the actions wait for the next
 * read (the fail-closed side).
 */
export function useSignInLockAnswered(
  uid: string | null,
  options: AccountReadOptions = {},
): boolean {
  const read = useMySecurity(uid, options);
  return servedData(read) !== undefined || (isGivmoError(read.error) && read.error.status === 404);
}

/**
 * What a fresh read says NOW: a lock binds (`locked`); none does, or the read is dark, as before
 * (`not_locked`); or the read failed and says nothing (`no_answer`).
 */
export type SignInLockNow = 'locked' | 'not_locked' | 'no_answer';

/**
 * Re-read the account's security read NOW and say whether a lock binds it. The act a page drew
 * before the lock bound (a stale tab) asks this before it starts anything: the fresh read lands in
 * the cache, so every surface on it redraws locked. A read that FAILS is `no_answer`, and the act
 * starts nothing and says it didn't work: falling back to the last answer decided on the very read
 * this one replaces, and stored a passkey under a lock (cut 1009b's review, F2). The dark 404 is
 * `not_locked`, as before. With no account known, `not_locked`: the server still refuses what it
 * refuses.
 */
export async function signInLockNow(
  queryClient: QueryClient,
  uid: string | null,
): Promise<SignInLockNow> {
  if (uid === null) return 'not_locked';
  const read = await fetchMySecurityNow(queryClient, uid);
  if (read.answer === 'failed') return 'no_answer';
  if (read.answer === 'dark') return 'not_locked';
  return signInLockFrom(read.security).active ? 'locked' : 'not_locked';
}

/** The words. Every one DRAFT (see the header). */
export const SIGN_IN_LOCK_WORDS = {
  /** DRAFT (new): the subject when the lock names no organization. */
  unnamedOrganization: 'Your organization',
  /** DRAFT (frame E1): iOS securityManagedSubtitle, with the reason the setup is gone. */
  cardLine: (organization: string) =>
    `${organization} manages how you sign in to Givmo, so passkeys and authenticator apps can’t be used or set up for now.`,
  /** DRAFT (frame E3): the card's line as the offer's title. */
  offerTitle: (organization: string) => `${organization} manages how you sign in to Givmo`,
  /** DRAFT (frame E3): under the offer's title. */
  offerLine: 'Passkeys and authenticator apps can’t be used or set up while it does.',
  /** DRAFT (frame E3): the offer's one way on, in place of "Not now". */
  ok: 'OK',
} as const;

function organizationOf(lock: SignInLock): string {
  return lock.organizationName ?? SIGN_IN_LOCK_WORDS.unnamedOrganization;
}

/** The Sign-in & security card's one line under a lock (frame E1). */
export function signInLockCardLine(lock: SignInLock): string {
  return SIGN_IN_LOCK_WORDS.cardLine(organizationOf(lock));
}

/**
 * The setup offer's title and line under a lock (frame E3), for a host that places the offer's
 * words above it, as Board's shell does with `passkeySetupOfferWords` otherwise.
 */
export function signInLockOfferWords(lock: SignInLock): { title: string; description: string } {
  return {
    title: SIGN_IN_LOCK_WORDS.offerTitle(organizationOf(lock)),
    description: SIGN_IN_LOCK_WORDS.offerLine,
  };
}
